import { useMemo, type ReactNode } from 'react';
import { useStore } from 'react-redux';
import {
  createRtkObjectTreeFacade,
  defineRtkObjectTreeModule,
  RtkObjectTreeProvider,
  type RtkObjectTreeFacade,
  type RtkObjectTreeMetricEvent,
  type RtkObjectTreeMode,
  type RtkObjectTreeQueryEndpoint,
} from '@triedge/rtk-abstractions';
import {
  type StudentsDashboardTree,
  StudentsDashboardTreeSchema,
} from '@triedge/students-families-domain';
import {
  type DomainRouterQueryArgs,
  type DomainRouterQueryData,
  type DomainRouterQueryName,
} from '../../api/domainRouterClient';
import {
  domainRouterUniversalApi,
  type DomainQueryRequest,
} from '../domainRouterUniversalApi';
import { studentsFamiliesObjectTreeRegistry } from '../../internal-owned/school-students-v2-1/objectTreeRegistry';

type StoreLike = {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
};

type WindowWithObjectTreeEvents = Window & {
  __YSTNJ_RTK_OBJECT_TREE_EVENTS__?: RtkObjectTreeMetricEvent[];
};

const ystnjRtkObjectTreeModeValues: readonly RtkObjectTreeMode[] = [
  'off',
  'shadow',
  'enabled',
];

const resolveYstnjRtkObjectTreeMode = (): RtkObjectTreeMode => {
  const rawMode = String(import.meta.env.VITE_YSTNJ_RTK_OBJECT_TREE_MODE ?? 'off').trim();
  return ystnjRtkObjectTreeModeValues.includes(rawMode as RtkObjectTreeMode)
    ? rawMode as RtkObjectTreeMode
    : 'off';
};

const recordYstnjRtkObjectTreeEvent = (event: RtkObjectTreeMetricEvent) => {
  if (typeof window === 'undefined') {
    return;
  }
  const target = window as WindowWithObjectTreeEvents;
  target.__YSTNJ_RTK_OBJECT_TREE_EVENTS__ = [
    ...(target.__YSTNJ_RTK_OBJECT_TREE_EVENTS__ ?? []),
    event,
  ].slice(-500);
};

const upsertDomainQueryData = domainRouterUniversalApi.util.upsertQueryData as unknown as (
  endpointName: 'domainQuery',
  args: DomainQueryRequest,
  payload: unknown,
) => unknown;

const createDomainQueryEndpoint = <
  TName extends DomainRouterQueryName,
  TResult = DomainRouterQueryData<TName>,
>(
  operation: TName,
): RtkObjectTreeQueryEndpoint<DomainRouterQueryArgs<TName>, TResult> => ({
  select: (args) => (
    domainRouterUniversalApi.endpoints.domainQuery.select({
      operation,
      args,
    } as DomainQueryRequest) as unknown as (state: unknown) => TResult | undefined
  ),
  initiate: (args, options) => domainRouterUniversalApi.endpoints.domainQuery.initiate({
    operation,
    args,
  } as DomainQueryRequest, options),
  upsertQueryData: (args, payload) => upsertDomainQueryData('domainQuery', {
    operation,
    args,
  } as DomainQueryRequest, payload),
});

const studentsDashboardEndpoint = createDomainQueryEndpoint<
  'getStudentsDashboardTree',
  StudentsDashboardTree
>('getStudentsDashboardTree');

export const ystnjRtkObjectTreeModules = [
  defineRtkObjectTreeModule({
    id: 'students-families',
    boundedContext: 'students-families',
    aggregateRoot: 'family',
    registry: studentsFamiliesObjectTreeRegistry,
    resources: {
      dashboard: {
        endpoint: studentsDashboardEndpoint,
        defaultArgs: { limit: 200, offset: 0 },
        seedFromBootstrap: (payload) => [{
          args: { limit: 200, offset: 0 },
          payload: StudentsDashboardTreeSchema.parse(payload),
        }],
      },
    },
    warmScopes: ['page', 'app', 'persona'],
  }),
] as const;

export const createYstnjRtkObjectTreeFacade = (
  store: StoreLike,
  mode: RtkObjectTreeMode = resolveYstnjRtkObjectTreeMode(),
): RtkObjectTreeFacade => createRtkObjectTreeFacade({
  modules: ystnjRtkObjectTreeModules,
  dispatch: store.dispatch,
  getState: store.getState,
  mode,
  instrumentation: {
    record: recordYstnjRtkObjectTreeEvent,
  },
});

export const YstnjRtkObjectTreeProvider = ({ children }: { children: ReactNode }) => {
  const store = useStore() as StoreLike;
  const mode = resolveYstnjRtkObjectTreeMode();
  const facade = useMemo(
    () => createYstnjRtkObjectTreeFacade(store, mode),
    [mode, store],
  );

  return (
    <RtkObjectTreeProvider value={facade}>
      {children}
    </RtkObjectTreeProvider>
  );
};
